/*
 * Lucid: Zubehoer (Metalldose, Cap) mit einem Tipp dazulegen - "Passt dazu"
 * im Warenkorb-Drawer (snippets/lucid-cart-addons.liquid). Gerendert in
 * Liquid, hier nur das Verhalten.
 *
 * NUR IM DRAWER (Franz, 17.10.2026: "Lasst uns die Metalldose am besten nur
 * im Card Drawer hinzufuegen."). Das Kaestchen "Dazu: Lucid Metalldose" in
 * der Buy-Box und der Block auf der Warenkorbseite sind wieder raus.
 *
 * Knopf "Hinzufuegen" -> /cart/add.js mit den Section-IDs aller
 * cart-items-component, danach der frische Warenkorb aus /cart.js und ein
 * CartAddEvent - dieselbe Reihenfolge wie Horizons Produktformular
 * (assets/product-form.js). Darauf zeichnen sich Drawer, Warenkorbseite und
 * Cart-Icon neu (component-cart-items.js morpht die mitgelieferten
 * Sections). Weil die Sections NACH dem Hinzufuegen gerendert sind, fehlt
 * die gekaufte Karte darin schon; ist nichts mehr anzubieten, faellt der
 * ganze Bereich weg. Kein eigenes Ein- und Ausblenden hier - welche Karte
 * steht, entscheidet allein Liquid (snippets/lucid-addon-state.liquid).
 *
 * KEINE SCHLEIFE, KEINE DOPPELTEN ZEILEN
 * - Dieses Modul schreibt nur auf einen Tipp, nie auf ein Event hin.
 * - assets/lucid-gifts.js hoert auf das CartAddEvent und gleicht danach seine
 *   Geschenkzeilen ab; bezahlte Zeilen fasst es nie an. Eine bezahlte Dose
 *   neben der Geschenk-Dose verhindert die Liquid-Regel, nicht dieses Skript.
 * - Ein zweiter Tipp waehrend der Anfrage tut nichts (aria-busy), und nach
 *   der Antwort ist die Karte weg.
 *
 * Quelle im Event: 'lucid-addons'. cart-drawer.js oeffnet darauf (auto-open)
 * wie bei jedem Hinzufuegen; im offenen Drawer ist das ein No-op.
 */
import { CartAddEvent } from '@theme/events';

export const ADDON_SOURCE = 'lucid-addons';

const ERROR_TEXT = 'Das hat nicht geklappt. Bitte noch einmal tippen.';

async function readCart() {
  const res = await fetch(`${Theme.routes.cart_url}.js`, {
    headers: { Accept: 'application/json' },
    cache: 'no-store',
  });
  if (!res.ok) throw new Error(`cart.js ${res.status}`);
  return res.json();
}

function cartItemsSectionIds() {
  const ids = new Set();
  for (const el of document.querySelectorAll('cart-items-component[data-section-id]')) {
    if (el instanceof HTMLElement && el.dataset.sectionId) ids.add(el.dataset.sectionId);
  }
  return [...ids];
}

/**
 * Artikelzahl aus einer gerenderten Warenkorb-Section - Rueckfall, wenn
 * /cart.js nach dem Hinzufuegen nicht antwortet (wie component-cart-items.js:
 * das versteckte [ref="cartItemCount"]).
 * @param {Record<string, string> | undefined} sections
 */
function itemCountFromSections(sections) {
  for (const html of Object.values(sections || {})) {
    if (!html) continue;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const text = doc.querySelector('[ref="cartItemCount"]')?.textContent;
    if (text) return parseInt(text, 10) || 0;
  }
  return undefined;
}

/** @param {HTMLButtonElement} button */
async function addFromCard(button) {
  if (button.getAttribute('aria-busy') === 'true') return;
  const card = button.closest('[data-lucid-addon]');
  const picked = card?.querySelector('input[type="radio"]:checked');
  const id = Number(picked instanceof HTMLInputElement ? picked.value : button.dataset.variantId);
  if (!id) return;

  const error = card?.querySelector('[data-lucid-addon-error]');
  if (error instanceof HTMLElement) error.hidden = true;
  button.setAttribute('aria-busy', 'true');

  try {
    const sectionIds = cartItemsSectionIds();
    const res = await fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: [{ id, quantity: 1 }],
        sections: sectionIds.join(','),
        sections_url: window.location.pathname,
      }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || data.status) throw new Error(data.description || data.message || `cart/add ${res.status}`);

    /** @type {any} */
    let cart = null;
    try {
      cart = await readCart();
    } catch (e) {
      cart = null;
    }
    const itemCount = cart && Number.isFinite(cart.item_count) ? cart.item_count : itemCountFromSections(data.sections);

    document.dispatchEvent(
      new CartAddEvent(cart ?? undefined, ADDON_SOURCE, {
        source: ADDON_SOURCE,
        itemCount,
        variantId: String(id),
        sections: data.sections,
      })
    );
  } catch (e) {
    // Die Karte steht noch (kein Neuzeichnen) - Fehler direkt darunter.
    if (error instanceof HTMLElement && error.isConnected) {
      error.textContent = ERROR_TEXT;
      error.hidden = false;
    }
  } finally {
    // Nach Erfolg ist der Knopf meist schon weggemorpht; sonst wieder frei.
    button.removeAttribute('aria-busy');
  }
}

// Knopf "Hinzufuegen" - per Delegation, weil der Warenkorb bei jedem Morph
// neue Knoten bekommt.
document.addEventListener('click', (event) => {
  const target = event.target instanceof Element ? event.target : null;
  const button = target?.closest('[data-lucid-addon-add]');
  if (!(button instanceof HTMLButtonElement)) return;
  event.preventDefault();
  addFromCard(button);
});

// Farbe der Cap: Bild und Preis der Karte nachziehen.
document.addEventListener('change', (event) => {
  const input = event.target;
  if (!(input instanceof HTMLInputElement) || input.type !== 'radio') return;
  const card = input.closest('[data-lucid-addon]');
  if (!card) return;
  const price = card.querySelector('[data-lucid-addon-price]');
  if (price && input.dataset.price) price.textContent = input.dataset.price;
  const img = card.querySelector('[data-lucid-addon-img]');
  if (img instanceof HTMLImageElement && input.dataset.image) {
    img.removeAttribute('srcset');
    img.src = input.dataset.image;
  }
});
